import {
	type FormEventHandler,
	type InputHTMLAttributes,
	type ReactNode,
	type RefObject,
	useEffect,
	useState,
} from 'react';

import { type OnInputEvent } from './helpers';

import { classNames } from '../../server/utils/classNames';

const sharedStyles = {
	inputContainer: classNames(
		'w-full relative bg-bg-primary flex flex-row items-center px-4 py-3 rounded-lg border-2 border-border-primary transition-shadow ring-transparent ring-[6px]',
		'focus-within:border-content-primary focus-within:ring-bg-tertiary hover:ring-bg-tertiary',
	),
	eyelid: 'text-content-secondary absolute transition-all',
	eyelidRaised: 'text-body-xs',
	eyelidLowered: 'text-body',
	hint: 'text-body-xs text-content-secondary mt-2',
	error: 'text-body-xs text-util-alert-500 mt-2',
};

const disabledStyles = {
	inputContainer: 'bg-accent-grey-100 cursor-not-allowed hover:ring-transparent',
	eyelidLowered: 'text-content-disabled-primary',
};

const invalidStyles = {
	inputContainer: classNames(
		'bg-util-alert-100 border-util-alert-500 focus-within:border-util-alert-500 hover:ring-util-alert-200 focus-within:ring-util-alert-200',
	),
	eyelid: 'text-util-alert-500',
};

export type BaseInputProps<T> = Omit<InputHTMLAttributes<T>, 'id'>;

export type InputProps<T> = BaseInputProps<T> & {
	/** The visual label for the input. This property is also used as label's htmlFor and input's id attributes to enhance acessibility.  */
	label: string;
	endAdornment?: ReactNode;
	error?: ReactNode;
	hint?: ReactNode;
	invalid?: boolean;
	onAutoFill?: (e: CustomEvent<T>) => void;
};

type InputPassedProps<T> = Omit<InputHTMLAttributes<T>, 'onInput' | 'onChange' | 'onFocus' | 'onBlur'>;

type UseInputFocusBehaviorParams<T extends HTMLInputElement | HTMLTextAreaElement> = {
	forwardedRef: RefObject<T | null>;
	props: InputPassedProps<T>;
	onChange: React.ChangeEventHandler<T> | undefined;
	onFocus: React.FocusEventHandler<T> | undefined;
	onBlur: React.FocusEventHandler<T> | undefined;
};

const useInputFocusBehavior = <T extends HTMLInputElement | HTMLTextAreaElement>({
	forwardedRef,
	onBlur,
	onChange,
	onFocus,
	props,
}: UseInputFocusBehaviorParams<T>) => {
	const [focused, setFocused] = useState(props.autoFocus);
	const [isEmpty, setIsEmpty] = useState(!(props.defaultValue || props.value));
	const shouldRaiseEyelid = focused || !isEmpty;

	useEffect(() => {
		if (props.defaultValue || props.value) {
			setIsEmpty(false);
		} else {
			setIsEmpty(true);
		}

		if (document.hasFocus() && forwardedRef?.current?.contains(document.activeElement)) {
			setFocused(true);
		}
	}, [props.value, props.defaultValue, forwardedRef]);

	const handleBlur = (e: React.FocusEvent<T>) => {
		setFocused(false);
		onBlur?.(e);
	};

	const handleChange = (e: React.ChangeEvent<T>) => {
		setIsEmpty(!e.target.value);
		onChange?.(e);
	};

	const handleFocus = (e: React.FocusEvent<T>) => {
		setFocused(true);
		onFocus?.(e);
	};

	return {
		handleFocus,
		handleBlur,
		handleChange,
		shouldRaiseEyelid,
	};
};

type RenderFunctionParams<T extends HTMLInputElement | HTMLTextAreaElement> = InputPassedProps<T> & {
	handleFocus: (e: React.FocusEvent<T>) => void;
	handleBlur: (e: React.FocusEvent<T>) => void;
	handleChange: (e: React.ChangeEvent<T>) => void;
	handleOnInputChange: (e: OnInputEvent<T>) => void;
};

type Props<T extends HTMLInputElement | HTMLTextAreaElement> = InputProps<T> & {
	forwardedRef: RefObject<T | null>;
	render: (params: RenderFunctionParams<T>) => ReactNode;
	getEyelidClassName: (shouldRaiseEyelid: boolean) => string;
	containerClassName?: string;
};

export const InputController = <T extends HTMLInputElement | HTMLTextAreaElement>({
	label,
	error,
	hint,
	endAdornment,
	disabled,
	forwardedRef,
	render,
	onAutoFill,
	onInput = () => {},
	containerClassName,
	invalid,
	getEyelidClassName,
	onChange,
	onFocus,
	onBlur,
	...rest
}: Props<T>) => {
	const { handleBlur, handleChange, handleFocus, shouldRaiseEyelid } = useInputFocusBehavior<T>({
		forwardedRef,
		onBlur,
		onChange,
		onFocus,
		props: rest,
	});

	const handleOnInputChange = (e: OnInputEvent<T>) => {
		handleOnInput(e, onInput, onAutoFill);
	};

	const inputBoxClasses = classNames(
		sharedStyles.inputContainer,
		{ [disabledStyles.inputContainer]: disabled },
		{ [invalidStyles.inputContainer]: invalid && !disabled },
		containerClassName,
	);

	const eyelidClasses = classNames(
		sharedStyles.eyelid,
		{ [invalidStyles.eyelid]: invalid && !disabled },
		{ [sharedStyles.eyelidRaised]: shouldRaiseEyelid },
		{ [sharedStyles.eyelidLowered]: !shouldRaiseEyelid },
		{ [disabledStyles.eyelidLowered]: disabled && !shouldRaiseEyelid },
		getEyelidClassName(shouldRaiseEyelid),
	);

	return (
		<label htmlFor={label} className="w-full">
			<div className={inputBoxClasses} data-testid="input-container">
				<div className={eyelidClasses} data-state={shouldRaiseEyelid ? 'up' : 'down'} data-testid="input-eyelid">
					{label}
				</div>
				{render({ handleBlur, handleChange, handleFocus, handleOnInputChange, disabled, id: label, ...rest })}
				{endAdornment}
			</div>
			{invalid && error && (
				<div role="alert" aria-live="assertive" className={sharedStyles.error}>
					<span>{error}</span>
				</div>
			)}
			{hint && <div className={sharedStyles.hint}>{hint}</div>}
		</label>
	);
};

export function autoComplete<T extends HTMLInputElement | HTMLTextAreaElement>(
	element: T,
	callback: (e: CustomEvent) => void,
) {
	if (element.hasAttribute('autofilled')) {
		return;
	}
	element.setAttribute('autofilled', 'true');

	const event = new window.CustomEvent('autofill', {
		bubbles: true,
		cancelable: true,
		detail: {
			autoFilled: true,
		},
	});

	// no autofill if preventDefault is called
	if (!element.dispatchEvent(event)) {
		element.value = '';
	}

	if (callback) {
		callback(event);
	}
}

export function cancelAutoComplete<T extends HTMLInputElement | HTMLTextAreaElement>(
	element: T,
	callback: (e: CustomEvent) => void,
) {
	if (!element.hasAttribute('autofilled')) {
		return;
	}
	element.removeAttribute('autofilled');
	const event = new window.CustomEvent('autofill', {
		bubbles: true,
		cancelable: false,
		detail: {
			autoFilled: false,
		},
	});
	element.dispatchEvent(event);
	if (callback) {
		callback(event);
	}
}

type OnInput<T> = FormEventHandler<T>;
type OnAutoFill = ((e: CustomEvent) => void) | undefined;

export const handleOnInput = <T extends HTMLInputElement | HTMLTextAreaElement>(
	e: OnInputEvent<T>,
	onInput: OnInput<T>,
	onAutoFill: OnAutoFill,
) => {
	onInput(e);
	if (onAutoFill) {
		const { nativeEvent } = e;
		if ('insertReplacementText' === e.inputType || !('data' in nativeEvent)) {
			autoComplete(e.target, onAutoFill);
		} else {
			cancelAutoComplete(e.target, onAutoFill);
		}
	}
};
